import { Events } from '../events';
import { safeReleasePointerCapture } from '../safe-pointer-capture';
import { normalizeAnnotationPriority } from './annotation-priority';
import { RfiRecord } from './rfi-panel';

function h(tag: string, cls?: string, attrs?: Record<string, string>): HTMLElement {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (attrs) {
        for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    }
    return el;
}

function downloadCsvSvg(): SVGSVGElement {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('class', 'annotation-modal-download-csv-icon');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(ns, 'path');
    path.setAttribute(
        'd',
        'M8 1.5v7.2M5.2 5.9 8 8.7l2.8-2.8M3 11.5h10',
    );
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
    return svg;
}

function gripSvg(): SVGSVGElement {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('class', 'annotation-list-grip-icon');
    for (const y of [4, 8, 12]) {
        const line = document.createElementNS(ns, 'line');
        line.setAttribute('x1', '3');
        line.setAttribute('y1', String(y));
        line.setAttribute('x2', '13');
        line.setAttribute('y2', String(y));
        line.setAttribute('stroke', 'currentColor');
        line.setAttribute('stroke-width', '1.5');
        line.setAttribute('stroke-linecap', 'round');
        svg.appendChild(line);
    }
    return svg;
}

/** Drop index 0..n where n = row count (after last slot). */
function computeDropIndex(body: HTMLElement, clientY: number): number {
    const rows = Array.from(body.querySelectorAll('.annotation-list-row:not(.is-ghost)')) as HTMLElement[];
    for (let i = 0; i < rows.length; i++) {
        const rect = rows[i].getBoundingClientRect();
        const mid = rect.top + rect.height / 2;
        if (clientY < mid) return i;
    }
    return rows.length;
}

/**
 * DOM-based annotation list modal. Mounted once; shown/hidden via events.
 * The edit modal was removed — title editing is now inline via the hover card.
 */
class AnnotationModalManager {
    private host: HTMLElement;
    private events: Events;

    constructor(events: Events, canvasContainer: HTMLElement) {
        this.events = events;
        this.host = h('div');
        this.host.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:150;';
        canvasContainer.appendChild(this.host);

        events.on('annotation.openListModal', (records: RfiRecord[]) => this.showListModal(records));

        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'touchstart', 'touchmove', 'touchend'].forEach((name) => {
            this.host.addEventListener(name, (e: Event) => e.stopPropagation());
        });
    }

    private clear() {
        this.host.innerHTML = '';
    }

    private showListModal(records: RfiRecord[]) {
        this.clear();

        const overlay = h('div', 'annotation-modal-overlay');
        overlay.style.pointerEvents = 'auto';
        const modal = h('div', 'annotation-modal');

        const header = h('div', 'annotation-modal-header');
        const title = h('span', 'annotation-modal-title');
        title.textContent = `Annotations (${records.length})`;

        const headerRight = h('div', 'annotation-modal-header-right');

        const canExport = this.events.invoke('rfi.canExport') !== false;
        const rfiRecords = records.filter((r) => !r.kind || r.kind === 'rfi');
        const rfiCount = rfiRecords.length;

        let workingIds = records.map((r) => r.id);
        let originalIds = records.map((r) => r.id);

        const csvBtn = h('button', 'annotation-modal-download-csv') as HTMLButtonElement;
        csvBtn.type = 'button';
        csvBtn.title = rfiCount === 0 ? 'No RFI annotations' : 'Download CSV';
        csvBtn.setAttribute('aria-label', 'Download annotation CSV');
        csvBtn.appendChild(downloadCsvSvg());
        if (!canExport) {
            csvBtn.style.display = 'none';
        } else {
            csvBtn.disabled = rfiCount === 0;
            csvBtn.classList.toggle('is-disabled', rfiCount === 0);
            csvBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                if (csvBtn.disabled || rfiCount === 0) return;
                const orderedRfiIds = workingIds.filter((id) => {
                    const rec = records.find((r) => r.id === id);
                    return rec && (!rec.kind || rec.kind === 'rfi');
                });
                this.events.fire('volugraph.requestAnnotationCsv', { orderedIds: orderedRfiIds });
            });
        }

        const closeBtn = h('button', 'annotation-modal-close');
        closeBtn.textContent = '\u00D7';
        closeBtn.addEventListener('click', () => {
            if (editMode && orderChanged) {
                this.events.fire('volugraph.annotationReorder', workingIds.slice());
            }
            this.clear();
            this.events.fire('annotation.listVisible', false);
        });

        header.appendChild(title);
        if (canExport) {
            headerRight.appendChild(csvBtn);
        }
        headerRight.appendChild(closeBtn);
        header.appendChild(headerRight);

        const body = h('div', 'annotation-list-modal-body');

        let editMode = false;
        let orderChanged = false;
        workingIds = records.map((r) => r.id);
        originalIds = records.map((r) => r.id);

        /** Blocks row click after a drag gesture (non-edit list) */
        let rowDragActive = false;

        /** Pointer-drag state (edit mode only) */
        let ghostEl: HTMLElement | null = null;
        let activeDragId: string | null = null;
        let dragPointerId: number | null = null;
        let startClientY = 0;
        let onPointerMoveBound: ((e: PointerEvent) => void) | null = null;
        let onPointerUpBound: ((e: PointerEvent) => void) | null = null;

        let renderRows: () => void = () => {};

        const endDrag = (commitPointer = true) => {
            if (dragPointerId !== null && body.hasPointerCapture(dragPointerId)) {
                safeReleasePointerCapture(body, dragPointerId);
            }
            dragPointerId = null;
            activeDragId = null;
            rowDragActive = false;
            if (ghostEl) {
                ghostEl.remove();
                ghostEl = null;
            }
            if (onPointerMoveBound) {
                body.removeEventListener('pointermove', onPointerMoveBound);
                onPointerMoveBound = null;
            }
            if (onPointerUpBound) {
                body.removeEventListener('pointerup', onPointerUpBound);
                body.removeEventListener('pointercancel', onPointerUpBound);
                onPointerUpBound = null;
            }
            if (commitPointer) {
                renderRows();
            }
        };

        if (records.length === 0) {
            const empty = h('div', 'annotation-list-empty');
            empty.textContent = 'No annotations yet. Use the T+ tool to add one.';
            body.appendChild(empty);
        } else {
            const selectedRec = this.events.invoke('annotation.getSelected') as RfiRecord | null;

            const editOrderBtn = h('button', 'annotation-modal-edit-order-btn');
            editOrderBtn.textContent = 'EDIT ORDER';
            headerRight.insertBefore(editOrderBtn, closeBtn);

            renderRows = () => {
                if (ghostEl) {
                    ghostEl.remove();
                }
                body.innerHTML = '';
                workingIds.forEach((recId, idx) => {
                    const rec = records.find((r) => r.id === recId);
                    if (!rec) return;
                    const isPh = editMode && activeDragId === rec.id;
                    const row = h(
                        'div',
                        `annotation-list-row${selectedRec?.id === rec.id ? ' is-selected' : ''}${editMode ? ' is-edit-mode' : ''}${isPh ? ' is-placeholder' : ''}`,
                    );
                    row.dataset.annotationId = rec.id;

                    const num = h('span', 'annotation-list-row-number');
                    num.textContent = String(idx + 1);
                    const name = h('span', 'annotation-list-row-title');
                    name.textContent = rec.title || `Annotation ${idx + 1}`;
                    const dot = h('span', `annotation-list-row-status status-${normalizeAnnotationPriority(rec.status)}`);
                    row.appendChild(num);
                    row.appendChild(name);
                    row.appendChild(dot);

                    if (editMode) {
                        const grip = h('span', 'annotation-list-row-grip');
                        grip.appendChild(gripSvg());
                        row.appendChild(grip);
                    }

                    if (!editMode) {
                        row.addEventListener('click', () => {
                            if (rowDragActive) return;
                            this.events.fire('annotation.selectById', rec.id);
                            this.events.fire('rfi.flyTo', rec.id);
                            this.clear();
                            this.events.fire('annotation.listVisible', false);
                        });
                    }
                    body.appendChild(row);
                });

                if (editMode && activeDragId && ghostEl) {
                    body.appendChild(ghostEl);
                    const ph = body.querySelector('.annotation-list-row.is-placeholder') as HTMLElement | null;
                    const br = body.getBoundingClientRect();
                    if (ph) {
                        ghostEl.style.left = `${ph.offsetLeft}px`;
                        ghostEl.style.width = `${ph.offsetWidth}px`;
                        const gh = ghostEl.offsetHeight || ph.offsetHeight;
                        const top = startClientY - br.top + body.scrollTop - gh / 2;
                        const maxTop = Math.max(0, body.scrollHeight - gh);
                        ghostEl.style.top = `${Math.max(0, Math.min(top, maxTop))}px`;
                    }
                    ghostEl.style.transform = 'scale(1.03)';
                }
            };

            const tryReorderFromPointer = (e: PointerEvent) => {
                if (!activeDragId) return;
                const from = workingIds.indexOf(activeDragId);
                if (from < 0) return;

                const insertAt = computeDropIndex(body, e.clientY);
                const next = workingIds.slice();
                const [item] = next.splice(from, 1);
                let dest = insertAt;
                if (insertAt > from) dest--;
                next.splice(dest, 0, item);

                const changed = next.some((id, i) => id !== workingIds[i]);
                if (!changed) return;

                workingIds.length = 0;
                workingIds.push(...next);
                orderChanged = workingIds.some((id, i) => id !== originalIds[i]);
                startClientY = e.clientY;
                renderRows();
            };

            const onPointerMove = (e: PointerEvent) => {
                if (dragPointerId === null || e.pointerId !== dragPointerId || !ghostEl || !activeDragId) return;
                e.preventDefault();
                const dy = e.clientY - startClientY;
                ghostEl.style.transform = `translateY(${dy}px) scale(1.03)`;
                tryReorderFromPointer(e);
            };

            const onPointerUp = (e: PointerEvent) => {
                if (dragPointerId === null || e.pointerId !== dragPointerId) return;
                e.preventDefault();
                endDrag(true);
            };

            body.addEventListener(
                'pointerdown',
                (e: PointerEvent) => {
                    if (!editMode || e.button !== 0) return;
                    const target = e.target as HTMLElement | null;
                    const row = target?.closest?.('.annotation-list-row.is-edit-mode') as HTMLElement | null;
                    if (!row || row.classList.contains('is-placeholder') || row.classList.contains('is-ghost')) return;
                    const id = row.dataset.annotationId;
                    if (!id) return;

                    e.preventDefault();
                    e.stopPropagation();

                    activeDragId = id;
                    dragPointerId = e.pointerId;
                    startClientY = e.clientY;

                    ghostEl = row.cloneNode(true) as HTMLElement;
                    ghostEl.classList.remove('is-selected', 'is-edit-mode');
                    ghostEl.classList.add('is-ghost');
                    row.classList.add('is-placeholder');

                    const br = body.getBoundingClientRect();
                    const rr = row.getBoundingClientRect();
                    ghostEl.style.width = `${rr.width}px`;
                    ghostEl.style.left = `${row.offsetLeft}px`;
                    const gh = row.offsetHeight;
                    const top = e.clientY - br.top + body.scrollTop - gh / 2;
                    const maxTop = Math.max(0, body.scrollHeight - gh);
                    ghostEl.style.top = `${Math.max(0, Math.min(top, maxTop))}px`;

                    body.appendChild(ghostEl);

                    try {
                        body.setPointerCapture(e.pointerId);
                    } catch {
                        /* ignore */
                    }

                    onPointerMoveBound = onPointerMove;
                    onPointerUpBound = onPointerUp;
                    body.addEventListener('pointermove', onPointerMoveBound);
                    body.addEventListener('pointerup', onPointerUpBound);
                    body.addEventListener('pointercancel', onPointerUpBound);
                    rowDragActive = true;
                },
                true,
            );

            editOrderBtn.addEventListener('click', () => {
                if (activeDragId) {
                    endDrag(true);
                }
                if (!editMode) {
                    editMode = true;
                    editOrderBtn.textContent = 'DONE';
                    editOrderBtn.classList.add('is-active');
                    modal.classList.add('is-edit-mode');
                } else {
                    editMode = false;
                    editOrderBtn.textContent = 'EDIT ORDER';
                    editOrderBtn.classList.remove('is-active');
                    modal.classList.remove('is-edit-mode');
                    if (orderChanged) {
                        this.events.fire('volugraph.annotationReorder', workingIds.slice());
                        orderChanged = false;
                        originalIds.length = 0;
                        originalIds.push(...workingIds);
                    }
                }
                renderRows();
            });

            renderRows();
        }

        modal.appendChild(header);
        modal.appendChild(body);
        overlay.appendChild(modal);
        this.host.appendChild(overlay);

        this.events.fire('annotation.listVisible', true);
    }
}

export { AnnotationModalManager };
